import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { UseMutateFunction } from "@tanstack/react-query";
import { Button, DatePicker, Statistic, Steps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import i18next from "i18next";
import { toast } from "react-toastify";
import styled from "styled-components";

import { CalculateOrderVariables } from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
  BSCoreWebRequestsResponsesDistributorsDistributorResponse,
} from "api/reactQuery/querySchemas";

import OrderTotals from "../OrderTotals";
import OrderPreviewModal from "./OrderPreviewModal";
import OrderProductsTabs from "./OrderProductsTab/OrderProductsTabs";
import SelectDistributorStep from "./Steps/SelectDistributorStep";
import SelectWarehouseStep from "./Steps/SelectWarehouseStep";
import { OrderInfoLabel } from "./Steps/styles";

const CountdownContainer = styled.div`
  position: sticky;
  display: flex;
  justify-content: center;
  width: 100%;
  z-index: 5;
  top: 55px;
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.semiLight};

  .ant-statistic-content {
    font-size: 28px;
    color: #d21919;
    padding: 3px 25px;
    background-color: #fed9d9;
    border-radius: 6px;
    font-family: Monospaced, math;
  }
`;

type Props = {
  resetCalculatedData: () => void;
  selectedDistributor: BSCoreWebRequestsResponsesDistributorsDistributorResponse;
  setSelectedDistributor: React.Dispatch<
    React.SetStateAction<BSCoreWebRequestsResponsesDistributorsDistributorResponse>
  >;
  selectedDepots: BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[];
  setSelectedDepots: React.Dispatch<
    React.SetStateAction<
      BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[]
    >
  >;
  deliveryDate: Dayjs;
  setDeliveryDate: React.Dispatch<React.SetStateAction<Dayjs>>;
  prepareOrder: () => void;
  saveAndSendDisabled: boolean;
  saveAndSendLoading: boolean;
  initialOrderLoading: boolean;
  showCountdown: boolean;
  dateCreated: Dayjs;
  showAllProductsAvailability: boolean;
  showMultipackProductsAvailability: boolean;
  showOnInvoiceProductsAvailability: boolean;
  showGasProductsAvailability: boolean;
  enableEditOrderedQuantities: boolean;
  orderData: BSCoreServicesDtoMarketplaceOrderDto;
  calculateOrder: UseMutateFunction<
    BSCoreServicesDtoMarketplaceOrderDto,
    {
      status: "unknown";
      payload: string;
    },
    CalculateOrderVariables,
    unknown
  >;
  calculatedOrderResponse: BSCoreServicesDtoMarketplaceOrderDto;
  isPreviewOrderModalOpen: boolean;
  setIsPreviewOrderModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  completeOrder: () => Promise<void>;
  savedOrderResponse: BSCoreServicesDtoMarketplaceOrderDto;
  setOrderComment: Dispatch<SetStateAction<string>>;
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
};

const ORDER_TIME_IN_MINUTES = 60;

const OrderInfoSection: React.FC<Props> = ({
  resetCalculatedData,
  selectedDepots,
  setSelectedDistributor,
  selectedDistributor,
  setSelectedDepots,
  setDeliveryDate,
  deliveryDate,
  prepareOrder,
  saveAndSendDisabled,
  saveAndSendLoading,
  initialOrderLoading,
  showCountdown,
  dateCreated,
  showAllProductsAvailability,
  showMultipackProductsAvailability,
  showOnInvoiceProductsAvailability,
  showGasProductsAvailability,
  enableEditOrderedQuantities,
  orderData,
  calculateOrder,
  calculatedOrderResponse,
  isPreviewOrderModalOpen,
  setIsPreviewOrderModalOpen,
  completeOrder,
  savedOrderResponse,
  setOrderComment,
  step,
  setStep,
}) => {
  const onCountdownFinish = () => {
    toast.warn(
      i18next.t("your_order_session_is_over_and_your_order_has_been_deleted.")
    );
  };

  const [isNextDisabled, setIsNextDisabled] = useState(true);

  const handleNext = () => {
    if (step !== steps.length - 1) {
      return setStep(step + 1);
    }

    // Last step - check current availability and prepare the order preview
    prepareOrder();
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleCancelPreview = () => {
    setIsPreviewOrderModalOpen(false);
  };

  useEffect(() => {
    if (step === 0) {
      return setIsNextDisabled(selectedDistributor ? false : true);
    } else if (step === 1) {
      setIsNextDisabled(
        initialOrderLoading ||
          !selectedDepots ||
          selectedDepots?.length === 0 ||
          !deliveryDate
      );
    } else if (step === 2) {
      setIsNextDisabled(initialOrderLoading || saveAndSendDisabled);
    }
  }, [
    selectedDepots,
    deliveryDate,
    step,
    saveAndSendDisabled,
    selectedDistributor,
    initialOrderLoading,
  ]);

  const steps = [
    {
      content: (
        <SelectDistributorStep
          setDeliveryDate={setDeliveryDate}
          setSelectedDepots={setSelectedDepots}
          setSelectedDistributor={setSelectedDistributor}
          resetCalculatedData={resetCalculatedData}
          selectedDistributor={selectedDistributor}
        />
      ),
      title: i18next.t("distributor"),
    },
    {
      content: (
        <SelectWarehouseStep
          resetCalculatedData={resetCalculatedData}
          selectedDistributor={selectedDistributor}
          selectedDepots={selectedDepots}
          setSelectedDepots={setSelectedDepots}
          saveAndSendLoading={saveAndSendLoading}
          setDeliveryDate={setDeliveryDate}
          deliveryDate={deliveryDate}
        />
      ),
      title: i18next.t("depot"),
    },
    {
      content: (
        <>
          {showCountdown && dateCreated && (
            <CountdownContainer>
              <Statistic.Countdown
                prefix={i18next.t("time_left")}
                format="mm:ss"
                value={dateCreated
                  ?.add(ORDER_TIME_IN_MINUTES, "minutes")
                  .valueOf()}
                onFinish={onCountdownFinish}
              />
            </CountdownContainer>
          )}

          <TitleAndDateWrapper>
            <h1>{i18next.t("select_products")}</h1>

            <DateWrapper>
              <OrderInfoLabel>{i18next.t("delivery_date")}</OrderInfoLabel>
              <DatePicker
                showNow={false}
                allowClear={false}
                value={deliveryDate}
                disabled={true}
                onChange={(date) => {
                  setDeliveryDate(date);
                }}
                minDate={dayjs().add(1, "day")}
                format="DD/MM/YYYY"
              />
            </DateWrapper>
          </TitleAndDateWrapper>

          {!!orderData?.total && !!deliveryDate && (
            <OrderTotals
              totals={orderData.total}
              isTotalWeightInLimit={
                calculatedOrderResponse
                  ? calculatedOrderResponse.isTotalWeightInLimit
                  : true
              }
              isTruckAreaFactorInLimit={
                calculatedOrderResponse
                  ? calculatedOrderResponse.isTruckAreaFactorInLimit
                  : true
              }
            />
          )}
          <OrderProductsTabs
            showAllProductsAvailability={showAllProductsAvailability}
            showMultipackProductsAvailability={
              showMultipackProductsAvailability
            }
            showOnInvoiceProductsAvailability={
              showOnInvoiceProductsAvailability
            }
            enableEditOrderedQuantities={enableEditOrderedQuantities}
            orderData={orderData}
            calculateOrder={calculateOrder}
            selectedDepots={selectedDepots}
            showGasProductsAvailability={showGasProductsAvailability}
          />

          <OrderPreviewModal
            isOpen={isPreviewOrderModalOpen}
            onSubmit={completeOrder}
            isLoading={saveAndSendLoading}
            onCancel={handleCancelPreview}
            data={savedOrderResponse}
            setOrderComment={setOrderComment}
            selectedDepots={selectedDepots}
          />
        </>
      ),
      title: i18next.t("order"),
    },
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          size="large"
          disabled={step === 0}
          style={{ margin: "0 8px" }}
          onClick={handlePrev}
        >
          {i18next.t("back_button")}
        </Button>
        <StyledSteps current={step} items={items} />
        <Button
          size="large"
          disabled={isNextDisabled}
          type="primary"
          onClick={handleNext}
        >
          {i18next.t("next_button")}
        </Button>
      </div>

      <div style={{ marginTop: 24 }}>{steps[step].content}</div>
    </>
  );
};

export default OrderInfoSection;

const StyledSteps = styled(Steps)`
  align-items: center;
  width: 480px;
  .ant-steps-item-finish .ant-steps-item-icon {
    background: #9bd9ac;
    border: #9bd9ac;
    color: white;
  }

  .ant-steps-item-process .ant-steps-item-icon {
    background: #818181;
    border: #818181;
    color: white;
    font-weight: bold;
  }

  .ant-steps-finish-icon {
    color: white;
  }

  .ant-steps-item
    > .ant-steps-item-container
    > .ant-steps-item-content
    > .ant-steps-item-title::after {
    background: transparent !important;
  }
`;

const TitleAndDateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h1 {
    font-size: 36px;
  }
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
